import { Divider, Stack, Modal, Alert, Button } from "@mantine/core";
import { useDisclosure } from '@mantine/hooks';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import CurrentPlan from "./components/CurrentPlan";
import { useEffect, useState } from "react";
import UpgradePlans from "./components/UpgradePlans";
import { notifications } from '@mantine/notifications';
import PaymentService from "../../services/PaymentService";
import { AdvancedPaymentInformation, BasicPaymentInformation, PaymentInformation } from "common";
import { format, set } from "date-fns";
import { it } from "date-fns/locale";
import AccomodationService from "../../services/AccomodationService";
import UserProfileService from "../../services/UserProfileService";
import { login } from "../../store/features/authSlice";
import { TOKEN_EXP } from "../../pages/LoginPage/LoginPage";
import { IconArrowRight, IconInfoCircle } from "@tabler/icons-react";
import PlanCard from "./components/PlanCard";
import { useBreakpoints } from "../../utils/useBreakpoints";

export type SubscriptionPlan = {
    name: "monthly" | "annual" | "ltd-schedine-cloud" | "ltd-self-checkin",
    label: string,
    price: number,
    features: string[],
    isSelected: boolean,
    isCurrentPlan?: boolean,
};

const Subscriptions = () => {
    const user = useSelector((state: RootState) => state.auth);
    const [opened, { open, close }] = useDisclosure(false);
    const dispatch = useDispatch();

    const [ loading, setLoading ] = useState<{ name: string, state: boolean}>({ name: "", state: false });
    const [ planSelected, setPlanSelected ] = useState<SubscriptionPlan>();
    const [ message, setMessage ] = useState("");
    const [ isValidPurchase, setIsValidPurchase ] = useState(false);
    const [ ltds, setLtds ] = useState<AdvancedPaymentInformation[]>([]);

    const [ plans, setPlans ] = useState<SubscriptionPlan[]>([
        {
            name: "monthly",
            label: "Mensile",
            price: 3.99,
            features: [
                "Scansione documento con AI",
                "Checkins illimitati",
                "Storico dei checkins",
            ],
            isSelected: false
        },
        {
            name: "annual",
            label: "Annuale",
            price: 43,
            features: [
                "Sconto del 10%",
                "Scansione documento con AI",
                "Checkins illimitati",
                "Storico dei checkins"
            ],
            isSelected: false
        }
    ]);

    useEffect(() => {
        const schedineCloud = (user.attrs.payment as PaymentInformation).subscriptionHistory
            .find((p) => p.type === "ltd_schedinecloud");
        const selfCheckin = (user.attrs.payment as PaymentInformation).subscriptionHistory
            .find((p) => p.type === "ltd_selfcheckin");

        const found: AdvancedPaymentInformation[] = [];
        if(schedineCloud) found.push(schedineCloud as AdvancedPaymentInformation);
        if(selfCheckin) found.push(selfCheckin as AdvancedPaymentInformation);
        setLtds(found);
    }, [ user ]);

    useEffect(() => {
        if(user.attrs?.referredBy && user.attrs.referredBy.length > 0)
        {
            const tmp = [ ...plans ];
            tmp[1].price = 38.7;
            tmp[1].features[0] = "Sconto 10% + 10% codice invito";

            setPlans([ ...tmp ]);
        }
    }, [ user ]);

    const schedineCloudPlan: SubscriptionPlan = {
        name: "ltd-schedine-cloud",
        label: "SchedineCloud",
        price: 149,
        features: [
            "Download automatico schedine AlloggiatiWeb",
            "Archiviazione automatica per 5 anni",
            "Storico con ricerca per data"
        ],
        isSelected: false
    };

    const selfCheckinPlan: SubscriptionPlan = {
        name: "ltd-self-checkin",
        label: "Self Check-in",
        price: 199,
        features: [
            "Genera link per il self check-in degli ospiti",
            "Upload documenti da parte degli ospiti",
            "Template istruzioni personalizzabili"
        ],
        isSelected: false
    };

    const { isMobile } = useBreakpoints();

    const planClickHandler = async (selected: SubscriptionPlan) => {
        setLoading(curr => ({ ...curr, name: selected.name, state: true }));

        if(user.attrs.payment && (user.attrs.payment as PaymentInformation).subscriptionPlan?.status === "paused")
        {
            setIsValidPurchase(false);
            setMessage("Hai un abbonamento interrotto. Non puoi acquistarne un altro");
        }
        // Is current plan the same?
        else if(
            user.attrs.payment && 
            (user.attrs.payment as PaymentInformation).subscriptionPlan?.type === selected.name &&
            (user.attrs.payment as PaymentInformation).subscriptionPlan?.status === "active"
            )
        {
            setIsValidPurchase(false);

            setMessage(
                "Hai già questo piano attivo, in scadenza il " + 
                format(
                    (user.attrs.payment as PaymentInformation).subscriptionPlan?.endDate!, 
                    "dd MMMM yyyy", 
                    { locale: it }
                )
            );
        }
        // Invalid purchase (from 'monthly' to 'annual')
        else if(
            user.attrs.payment && 
            (user.attrs.payment as PaymentInformation).subscriptionPlan?.type === "annual" &&
            selected.name === "monthly" &&
            (user.attrs.payment as PaymentInformation).subscriptionPlan?.status === "active"
        )
        {
            setIsValidPurchase(false);

            setMessage(
                "Non puoi passare da un abbonamento Annuale ad uno Mensile. Il tuo piano attuale scadrà il " + 
                format(
                    (user.attrs.payment as PaymentInformation).subscriptionPlan?.endDate!, 
                    "dd MMMM yyyy", 
                    { locale: it }
                )
            );
        }
        // New purchase
        else if(
            user.attrs.payment && (user.attrs.payment as PaymentInformation).subscriptionPlan?.type === "trial"
            ||
            user.attrs.payment && (user.attrs.payment as PaymentInformation).subscriptionPlan?.status === "canceled"
            ||
            user.attrs.payment && (user.attrs.payment as PaymentInformation).subscriptionPlan?.status === "expired"
        )
        {
            setIsValidPurchase(true);

            // Check total rooms pre-calculation
            const userAccomodations = await AccomodationService.getAccomodations(user.token!);
            const totalRooms = userAccomodations.flatMap(a => a.rooms).length;

            if(userAccomodations.length === 0 || totalRooms === 0)
            {
                setIsValidPurchase(false);
                setMessage("Nessuna struttura trovata (0 camere). Usa l'app NowCheckin sul tuo telefono e configura prima una Struttura.");
            }
            else
            {
                setMessage(`Gestisci ${userAccomodations.length} strutture, per un totale di ${totalRooms} camere. L'importo da pagare sara': ${(totalRooms * selected.price).toFixed(2)}€ + IVA`);
            }
        }
        // Upgrade plan
        else
        {
            setIsValidPurchase(true);

            if((user.attrs.payment as PaymentInformation).subscriptionPlan?.status === "active")
                setMessage(`Stai aggiornando il tuo piano attivo con un altro.`);
        }

        setLoading(curr => ({ ...curr, name: "", state: false }));

        setPlanSelected(selected);
        open();     // Open modal
    };

    const purchasePlan = async () => {
        if(!planSelected) return;

        try 
        {
            setLoading(curr => ({ ...curr, name: planSelected.name, state: true }));
            
            const response = await PaymentService.subscribe(planSelected.name, user.token!);

            window.location.replace(response.ctx.url);
        } 
        catch (error: any) 
        {
            console.log("Error during purchase", error);
            
            notifications.show({
                color: 'red',
                title: 'Acquisto fallito',
                message: error.message
            });
        }
        finally
        {
            setLoading(curr => ({ ...curr, name: "", state: false }));
            close();    // Close modal
        }
    };

    const updateSubscription = async (status: AdvancedPaymentInformation["status"]) => {
        if(!user.attrs.payment) return;

        const r = window.confirm("Stai per aggiornare il tuo abbonamento. Procedi?");
        if(!r) return;

        try 
        {
            setLoading(curr => ({ ...curr, name: status, state: true }));
            const currentPlan = (user.attrs.payment as PaymentInformation).subscriptionPlan as AdvancedPaymentInformation;
            await PaymentService.updateSubscriptionStatus(currentPlan.billingSubscriptionId, status, user.token!);

            // Get user profile
            const up = await UserProfileService.getUserProfile(user.token!);
            dispatch(login({ ...up, token: user.token!, expiration: TOKEN_EXP }));

            notifications.show({
                color: 'green',
                title: 'Aggiornamento piano',
                message: "Il tuo abbonamento è stato aggiornato con successo"
            });
        } 
        catch (error: any) 
        {
            console.log("Error during plan pausing", error);

            notifications.show({
                color: 'red',
                title: 'Acquisto fallito',
                message: error.message,
                autoClose: false
            });
        }
        finally
        {
            setLoading(curr => ({ ...curr, name: "", state: false }));
        }
    };

    const purchaseLtd = (selected: SubscriptionPlan, ltdType: string) => {
        try 
        {
            setLoading(curr => ({ ...curr, name: "", state: true }));

            const existing = (user.attrs.payment as PaymentInformation).subscriptionHistory
                .find((p: BasicPaymentInformation) => p.type === ltdType);

            if(existing)
            {
                setIsValidPurchase(false);
                setMessage(`Hai già acquistato questo pacchetto`);
            }
            else
            {
                setMessage(selected.features.join(". "));
                setIsValidPurchase(true);
            }

            setPlanSelected(selected);
            open();
        } 
        catch (error: any) 
        {
            console.log("Error during plan selection", error);

            notifications.show({
                color: 'red',
                title: 'Selezione fallita',
                message: error.message,
                autoClose: false
            });
        }
        finally
        {
            setLoading(curr => ({ ...curr, name: "", state: false }));
        }
    };

    const updateLTD = async (plan: AdvancedPaymentInformation, status: AdvancedPaymentInformation["status"]) => {
        try {
            setLoading(curr => ({ ...curr, name: "", state: true }));
    
            const payment = user.attrs.payment as PaymentInformation;
            const currentPlanIndex = payment.subscriptionHistory.findIndex(
                (p: BasicPaymentInformation) => p.type === plan.type
            );
    
            if (currentPlanIndex === -1) throw new Error("Pacchetto non trovato");
    
            // Create a new payment object with the updated subscription history
            const updatedPayment = {
                ...payment,
                subscriptionHistory: [...payment.subscriptionHistory]
            };
    
            // Create a new plan object with the updated status
            updatedPayment.subscriptionHistory[currentPlanIndex] = {
                ...updatedPayment.subscriptionHistory[currentPlanIndex],
                status: status
            };
    
            await UserProfileService.saveProfileAttribute(
                { name: "payment", value: updatedPayment }, 
                user.token!
            );

            // Get user profile
            const up = await UserProfileService.getUserProfile(user.token!);
            dispatch(login({ ...up, token: user.token!, expiration: TOKEN_EXP }));
        } catch (error) {
            console.log("Error during plan pausing", error);

            notifications.show({
                color: 'red',
                title: 'Aggiornamento fallito',
                message: "Errore durante l'aggiornamento del pacchetto"
            });
        } finally {
            setLoading(curr => ({ ...curr, name: "", state: false }));
        }
    };    

    return(
        <>
            <Stack gap={0}>
                <div><h1>Abbonamenti</h1></div>

                <Divider my="xs" label="Piano attuale" labelPosition="center" />

                {
                    isMobile
                    ?
                    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                        <CurrentPlan 
                            plan={ user.attrs.payment.subscriptionPlan } 
                            loading={ loading } 
                            showActiveBtn={ user.attrs.payment.subscriptionPlan.status === "paused" }
                            onPause={ () => updateSubscription('paused') } 
                            onCancel={ () => updateSubscription('canceled') } 
                            onActive={ () => updateSubscription('active') }
                        />

                        {
                            ltds.map(p => 
                                <CurrentPlan 
                                    plan={ p } 
                                    loading={ loading } 
                                    showActiveBtn={ p.status === "paused" }
                                    ltd={ true }
                                    onPause={ () => updateLTD(p, "paused") } 
                                    onActive={ () => updateLTD(p, "active") }
                                    onCancel={ () => {} } 
                                    key={ p.billingSubscriptionId }
                                />
                            )
                        }
                    </div>
                    :
                    <div style={{ display: "flex", gap: "20px" }}>
                        <CurrentPlan 
                            plan={ user.attrs.payment.subscriptionPlan } 
                            loading={ loading } 
                            showActiveBtn={ user.attrs.payment.subscriptionPlan.status === "paused" }
                            onPause={ () => updateSubscription('paused') } 
                            onCancel={ () => updateSubscription('canceled') } 
                            onActive={ () => updateSubscription('active') }
                        />

                        {
                            ltds.map(p => 
                                <CurrentPlan 
                                    plan={ p } 
                                    loading={ loading } 
                                    showActiveBtn={ p.status === "paused" }
                                    ltd={ true }
                                    onPause={ () => updateLTD(p, "paused") } 
                                    onActive={ () => updateLTD(p, "active") }
                                    onCancel={ () => {} } 
                                    key={ p.billingPlanId }
                                />
                            )
                        }
                    </div>
                }

                <div style={{ marginTop: "40px" }}>
                    <Divider my="xs" label="Pacchetti Extra" labelPosition="center" />
                </div>

                <PlanCard 
                    plan={ schedineCloudPlan } 
                    type="Paghi una volta, Tua per sempre"
                    enphatize
                    loading={ loading }
                    key={ 0 } 
                    onPress={ selected => purchaseLtd(selected, "ltd_schedinecloud") } 
                />

                <div style={{ marginTop: 16 }} />

                <PlanCard 
                    plan={ selfCheckinPlan } 
                    type="Paghi una volta, Tua per sempre"
                    enphatize
                    loading={ loading }
                    key={ 1 } 
                    onPress={ selected => purchaseLtd(selected, "ltd_selfcheckin") } 
                />

                <div style={{ marginTop: "80px" }}>
                    <Divider my="xs" label="Aggiorna piano" labelPosition="center" />
                </div>

                <UpgradePlans plans={ plans } loading={ loading } onPress={ selected => planClickHandler(selected) } />
            </Stack>

            <Modal opened={ opened } onClose={ close } title={ `Conferma acquisto - Piano ${planSelected?.label}` } centered>
                <Stack>
                    <Alert variant="light" color="gray" radius="md" title="Riepilogo" icon={<IconInfoCircle />}>
                        { message }
                    </Alert>

                    <Button 
                        rightSection={ <IconArrowRight /> } 
                        loading={ loading.state } 
                        fullWidth 
                        disabled={ !isValidPurchase }
                        onClick={ purchasePlan }
                    >
                        Procedi
                    </Button>
                </Stack>
            </Modal>
        </>
    );
};

export default Subscriptions;